import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import Sidebar from '../Components/Sidebar/Sidebar';
import verificandoApp from '../../FuncoesGerais/verificaApp';
import verificandoToken from '../../FuncoesGerais/VerificarToken';
import {
    fetchRepositorio, fetchCategorias, fetchArquivos, fetchCommits, fetchVersoes,
    commitArquivos, restaurarVersao, excluirArquivo, baixarArquivo, lerArquivoBase64,
    updateRepositorio, createCategoria, updateCategoria, deleteCategoria
} from '../../api/repositorios';
import RepoArquivoDoc from './RepoArquivoDoc';
import './Repositorios.css';

// Cor de identidade por categoria (estável). Conhecidas têm cor fixa;
// novas recebem uma cor da paleta de forma determinística pelo id.
const COR_POR_CODIGO = { webservice: 'blue', regra: 'purple', tela_sgi: 'teal', relatorio: 'amber', outros: 'gray' };
const PALETA_CORES = ['blue', 'purple', 'teal', 'amber', 'pink', 'green', 'indigo', 'coral'];
function corCategoria(c) {
    if (!c || c.id == null) return 'gray';
    if (c.codigo && COR_POR_CODIGO[c.codigo]) return COR_POR_CODIGO[c.codigo];
    return PALETA_CORES[Math.abs(Number(c.id)) % PALETA_CORES.length];
}

// Ícone por extensão do arquivo (reconhecimento visual rápido).
const ICONE_POR_EXT = {
    ssf: 'fa-lock', xml: 'fa-file-code', br: 'fa-file-code', sql: 'fa-database',
    json: 'fa-file-code', js: 'fa-file-code', ts: 'fa-file-code', html: 'fa-file-code',
    css: 'fa-file-code', rtm: 'fa-file-invoice', rdp: 'fa-file-invoice', pdf: 'fa-file-pdf',
    doc: 'fa-file-word', docx: 'fa-file-word', xls: 'fa-file-excel', xlsx: 'fa-file-excel',
    csv: 'fa-file-csv', txt: 'fa-file-lines', log: 'fa-file-lines', zip: 'fa-file-zipper',
    png: 'fa-file-image', jpg: 'fa-file-image', jpeg: 'fa-file-image', gif: 'fa-file-image'
};
function iconeArquivo(nome) {
    const m = String(nome || '').match(/\.([a-z0-9]+)$/i);
    const ext = m ? m[1].toLowerCase() : '';
    return ICONE_POR_EXT[ext] || 'fa-file';
}

function formatarDataHora(d) {
    if (!d) return '—';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatarBytes(b) {
    const n = Number(b);
    if (!n || Number.isNaN(n)) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const ACAO_LABEL = { add: 'Criado', update: 'Atualizado', delete: 'Excluído', restore: 'Restaurado' };
const ACAO_BADGE = { add: 'bg-success', update: 'bg-primary', delete: 'bg-danger', restore: 'bg-warning text-dark' };

function Repositorio() {
    const history = useHistory();
    const { id } = useParams();
    const location = useLocation();
    const deepLinkFeito = useRef(false);

    const [repo, setRepo] = useState(null);
    const [categorias, setCategorias] = useState([]);
    const [arquivos, setArquivos] = useState([]);
    const [commits, setCommits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [aba, setAba] = useState('arquivos'); // arquivos | commits
    const [catSelecionada, setCatSelecionada] = useState(null); // categoria_id | null
    const [projSelecionado, setProjSelecionado] = useState(null); // string | null
    const [mensagem, setMensagem] = useState('');
    const [mensagemOk, setMensagemOk] = useState('');
    const [busca, setBusca] = useState('');
    // Filtros do Histórico (commits)
    const [filtroCommitCat, setFiltroCommitCat] = useState('');
    const [filtroCommitProj, setFiltroCommitProj] = useState('');
    const [filtroCommitArq, setFiltroCommitArq] = useState('');
    const [filtroDataIni, setFiltroDataIni] = useState('');
    const [filtroDataFim, setFiltroDataFim] = useState('');

    const [showEditar, setShowEditar] = useState(false);
    const [editNome, setEditNome] = useState('');
    const [editDescricao, setEditDescricao] = useState('');
    const [salvandoEdicao, setSalvandoEdicao] = useState(false);

    // commit modal
    const [showCommit, setShowCommit] = useState(false);
    const [commitFiles, setCommitFiles] = useState([]);
    const [commitCategoria, setCommitCategoria] = useState('');
    const [commitProjeto, setCommitProjeto] = useState('');
    const [commitMensagem, setCommitMensagem] = useState('');
    const [commitSenhaExclusao, setCommitSenhaExclusao] = useState('');
    const [enviando, setEnviando] = useState(false);

    // version drawer
    const [drawerArquivo, setDrawerArquivo] = useState(null);
    const [drawerAba, setDrawerAba] = useState('versoes'); // versoes | doc
    const [versoes, setVersoes] = useState([]);
    const [loadingVersoes, setLoadingVersoes] = useState(false);

    // atualizar versão (drawer)
    const [showAtualizarVersao, setShowAtualizarVersao] = useState(false);
    const [atualizarFile, setAtualizarFile] = useState(null);
    const [atualizarMensagem, setAtualizarMensagem] = useState('');
    const [enviandoAtualizacao, setEnviandoAtualizacao] = useState(false);

    // excluir arquivo (com senha)
    const [showExcluirArquivo, setShowExcluirArquivo] = useState(false);
    const [senhaExclusaoInput, setSenhaExclusaoInput] = useState('');
    const [excluindoArquivo, setExcluindoArquivo] = useState(false);

    // gestão de categorias (criar / renomear)
    const [showCatModal, setShowCatModal] = useState(false);
    const [catEditId, setCatEditId] = useState(null); // null = criar
    const [catNome, setCatNome] = useState('');
    const [salvandoCat, setSalvandoCat] = useState(false);

    const verificar = useCallback(async () => {
        const ok = await verificandoApp('ATENDENTE');
        if (!ok) history.push('/');
    }, [history]);

    const carregar = useCallback(async () => {
        verificandoToken();
        setLoading(true);
        try {
            const [r, cats, arqs, cmts] = await Promise.all([
                fetchRepositorio(id),
                fetchCategorias(),
                fetchArquivos(id),
                fetchCommits(id)
            ]);
            setRepo(r);
            setCategorias(Array.isArray(cats) ? cats : []);
            setArquivos(Array.isArray(arqs) ? arqs : []);
            setCommits(Array.isArray(cmts) ? cmts : []);
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao carregar repositório.');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { verificar(); carregar(); }, [verificar, carregar]);

    const termoBusca = busca.trim().toLowerCase();

    const arquivoCombinaBusca = useCallback((a, q) => {
        if (!q) return true;
        const partes = [
            a.caminho,
            a.projeto,
            a.categoria_nome,
            a.categoria_codigo,
        ].filter(Boolean).join(' ').toLowerCase();
        return partes.includes(q);
    }, []);

    const arquivosNaBusca = useMemo(() => {
        if (!termoBusca) return arquivos;
        return arquivos.filter((a) => arquivoCombinaBusca(a, termoBusca));
    }, [arquivos, termoBusca, arquivoCombinaBusca]);

    // Contagem por categoria (inclui "Sem categoria" quando houver).
    const categoriasComContagem = useMemo(() => {
        const base = arquivosNaBusca;
        const contagem = {};
        let semCat = 0;
        base.forEach((a) => {
            if (a.categoria_id == null) semCat += 1;
            else contagem[a.categoria_id] = (contagem[a.categoria_id] || 0) + 1;
        });
        const lista = categorias
            .map((c) => ({ ...c, total: contagem[c.id] || 0 }))
            .filter((c) => c.total > 0 || !termoBusca);
        if (semCat > 0) lista.push({ id: null, codigo: 'sem', nome: 'Sem categoria', icone: 'fa-question-circle', total: semCat });
        return lista;
    }, [categorias, arquivosNaBusca, termoBusca]);

    const projetosDaCategoria = useMemo(() => {
        const map = new Map();
        arquivosNaBusca
            .filter((a) => (catSelecionada === undefined ? true : a.categoria_id === catSelecionada))
            .forEach((a) => {
                const p = a.projeto || '(sem projeto)';
                map.set(p, (map.get(p) || 0) + 1);
            });
        return Array.from(map.entries()).map(([projeto, total]) => ({ projeto, total })).sort((a, b) => a.projeto.localeCompare(b.projeto));
    }, [arquivosNaBusca, catSelecionada]);

    const arquivosVisiveis = useMemo(() => {
        return arquivosNaBusca.filter((a) => {
            if (catSelecionada !== undefined && a.categoria_id !== catSelecionada) return false;
            if (projSelecionado != null) {
                const p = a.projeto || '(sem projeto)';
                if (p !== projSelecionado) return false;
            }
            return true;
        });
    }, [arquivosNaBusca, catSelecionada, projSelecionado]);

    // Opções dos filtros do Histórico, derivadas dos próprios commits.
    const opcoesHistorico = useMemo(() => {
        const cats = new Map(); const projs = new Set(); const arqs = new Set();
        commits.forEach((c) => {
            (c.categorias || []).forEach((nome, i) => {
                const cid = (c.categoria_ids || [])[i];
                if (nome) cats.set(nome, cid != null ? cid : nome);
            });
            (c.projetos || []).forEach((p) => p && projs.add(p));
            (c.arquivos || []).forEach((a) => a && arqs.add(a));
        });
        return {
            categorias: Array.from(cats.keys()).sort((a, b) => a.localeCompare(b)),
            projetos: Array.from(projs).sort((a, b) => a.localeCompare(b)),
            arquivos: Array.from(arqs).sort((a, b) => a.localeCompare(b)),
        };
    }, [commits]);

    const commitsFiltrados = useMemo(() => {
        const dIni = filtroDataIni ? new Date(filtroDataIni + 'T00:00:00') : null;
        const dFim = filtroDataFim ? new Date(filtroDataFim + 'T23:59:59') : null;
        return commits.filter((c) => {
            if (termoBusca) {
                const texto = [c.mensagem, c.autor_nome, ...(c.categorias || []), ...(c.projetos || []), ...(c.arquivos || [])]
                    .filter(Boolean).join(' ').toLowerCase();
                if (!texto.includes(termoBusca)) return false;
            }
            if (filtroCommitCat && !(c.categorias || []).includes(filtroCommitCat)) return false;
            if (filtroCommitProj && !(c.projetos || []).includes(filtroCommitProj)) return false;
            if (filtroCommitArq && !(c.arquivos || []).includes(filtroCommitArq)) return false;
            if (dIni || dFim) {
                const d = c.criado_em ? new Date(c.criado_em) : null;
                if (!d || Number.isNaN(d.getTime())) return false;
                if (dIni && d < dIni) return false;
                if (dFim && d > dFim) return false;
            }
            return true;
        });
    }, [commits, termoBusca, filtroCommitCat, filtroCommitProj, filtroCommitArq, filtroDataIni, filtroDataFim]);

    const projetosExistentes = useMemo(() => {
        const set = new Set();
        arquivos.forEach((a) => { if (a.projeto) set.add(a.projeto); });
        return Array.from(set).sort();
    }, [arquivos]);

    const caminhosArquivosAtivos = useMemo(() => {
        return new Set(arquivos.filter((a) => !a.excluido).map((a) => a.caminho));
    }, [arquivos]);

    const commitTemArquivoNovo = useMemo(() => {
        if (commitFiles.length === 0) return false;
        return commitFiles.some((f) => !caminhosArquivosAtivos.has(f.name));
    }, [commitFiles, caminhosArquivosAtivos]);

    const selecionarCategoria = (catId) => {
        setCatSelecionada(catId);
        setProjSelecionado(null);
    };

    const abrirDrawer = async (arquivo, abaInicial = 'versoes') => {
        setDrawerArquivo(arquivo);
        setDrawerAba(abaInicial);
        if (abaInicial === 'versoes') {
            setLoadingVersoes(true);
            try {
                const vs = await fetchVersoes(id, arquivo.id);
                setVersoes(Array.isArray(vs) ? vs : []);
            } finally {
                setLoadingVersoes(false);
            }
        } else {
            setVersoes([]);
        }
    };

    const fecharDrawer = () => { setDrawerArquivo(null); setVersoes([]); setDrawerAba('versoes'); };

    const navegarDocArquivo = async (arquivoId) => {
        const alvo = arquivos.find((x) => x.id === arquivoId);
        if (!alvo) {
            setMensagem('Arquivo mencionado não encontrado neste repositório.');
            return;
        }
        setCatSelecionada(alvo.categoria_id ?? null);
        setProjSelecionado(null);
        await abrirDrawer(alvo, 'doc');
    };

    // Deep-link: ?arquivo=ID abre direto o arquivo (seleciona a categoria dele
    // e abre o histórico). Usado pelo painel de Desenvolvimento no chamado.
    useEffect(() => {
        if (deepLinkFeito.current || loading || arquivos.length === 0) return;
        const params = new URLSearchParams(location.search);
        const arqParam = params.get('arquivo');
        if (!arqParam) return;
        const a = arquivos.find((x) => String(x.id) === String(arqParam));
        if (a) {
            setCatSelecionada(a.categoria_id ?? null);
            setProjSelecionado(null);
            const abaDoc = params.get('doc') === '1' || params.get('aba') === 'doc';
            abrirDrawer(a, abaDoc ? 'doc' : 'versoes');
        }
        deepLinkFeito.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, arquivos, location.search]);

    const enviarCommit = async () => {
        if (commitFiles.length === 0) { setMensagem('Selecione ao menos um arquivo.'); return; }
        if (commitTemArquivoNovo) {
            const senha = (commitSenhaExclusao || '').trim();
            if (senha.length < 4) {
                setMensagem('Informe uma senha de exclusão (mín. 4 caracteres) para os arquivos novos.');
                return;
            }
        }
        setEnviando(true);
        setMensagem('');
        try {
            const senhaPayload = commitTemArquivoNovo ? (commitSenhaExclusao || '').trim() : undefined;
            const arquivosPayload = await Promise.all(commitFiles.map(async (f) => ({
                caminho: f.name,
                nomArq: f.name,
                Arquivo64: await lerArquivoBase64(f),
                ...(senhaPayload ? { senha_exclusao: senhaPayload } : {})
            })));
            await commitArquivos(id, {
                mensagem: commitMensagem || null,
                categoria_id: commitCategoria || null,
                projeto: commitProjeto || null,
                ...(senhaPayload ? { senha_exclusao: senhaPayload } : {}),
                arquivos: arquivosPayload
            });
            setShowCommit(false);
            setCommitFiles([]);
            setCommitMensagem('');
            setCommitSenhaExclusao('');
            setCommitProjeto('');
            await carregar();
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao enviar arquivos.');
        } finally {
            setEnviando(false);
        }
    };

    const restaurar = async (arquivo, versao) => {
        verificandoToken();
        try {
            await restaurarVersao(id, arquivo.id, versao);
            await carregar();
            await abrirDrawer({ ...arquivo });
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao restaurar versão.');
        }
    };

    const abrirExcluirArquivo = () => {
        setMensagem('');
        setSenhaExclusaoInput('');
        setShowExcluirArquivo(true);
    };

    const confirmarExcluirArquivo = async () => {
        if (!drawerArquivo) return;
        const precisaSenha = !!drawerArquivo.tem_senha_exclusao;
        if (precisaSenha && !(senhaExclusaoInput || '').trim()) {
            setMensagem('Informe a senha de exclusão.');
            return;
        }
        verificandoToken();
        setExcluindoArquivo(true);
        setMensagem('');
        try {
            await excluirArquivo(id, drawerArquivo.id, precisaSenha ? senhaExclusaoInput : undefined);
            setShowExcluirArquivo(false);
            setSenhaExclusaoInput('');
            fecharDrawer();
            await carregar();
            setMensagemOk('Arquivo excluído. O histórico permanece para recuperação.');
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao excluir arquivo.');
        } finally {
            setExcluindoArquivo(false);
        }
    };

    const abrirAtualizarVersao = () => {
        setMensagem('');
        setAtualizarFile(null);
        setAtualizarMensagem('');
        setShowAtualizarVersao(true);
    };

    const enviarAtualizacaoVersao = async () => {
        if (!drawerArquivo) return;
        if (!atualizarFile) { setMensagem('Selecione um arquivo.'); return; }
        verificandoToken();
        setEnviandoAtualizacao(true);
        setMensagem('');
        const arquivoId = drawerArquivo.id;
        const caminho = drawerArquivo.caminho;
        const nomArq = caminho.split('/').pop() || caminho;
        try {
            await commitArquivos(id, {
                mensagem: atualizarMensagem || null,
                categoria_id: drawerArquivo.categoria_id ?? null,
                projeto: drawerArquivo.projeto || null,
                arquivos: [{
                    caminho,
                    nomArq,
                    Arquivo64: await lerArquivoBase64(atualizarFile),
                    categoria_id: drawerArquivo.categoria_id ?? null,
                    projeto: drawerArquivo.projeto || null,
                }]
            });
            setShowAtualizarVersao(false);
            setAtualizarFile(null);
            setAtualizarMensagem('');
            setMensagemOk('Nova versão registrada.');
            await carregar();
            const arqs = await fetchArquivos(id);
            const atualizado = (Array.isArray(arqs) ? arqs : []).find((a) => a.id === arquivoId);
            if (atualizado) await abrirDrawer(atualizado);
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao atualizar versão.');
        } finally {
            setEnviandoAtualizacao(false);
        }
    };

    const abrirModalCommit = () => {
        setMensagem('');
        setCommitSenhaExclusao('');
        setCommitCategoria(typeof catSelecionada === 'number' ? String(catSelecionada) : '');
        setCommitProjeto(projSelecionado && projSelecionado !== '(sem projeto)' ? projSelecionado : '');
        setShowCommit(true);
    };

    const abrirEditar = () => {
        setMensagem('');
        setMensagemOk('');
        setEditNome(repo?.nome || '');
        setEditDescricao(repo?.descricao || '');
        setShowEditar(true);
    };

    const abrirNovaCategoria = () => {
        setMensagem('');
        setCatEditId(null);
        setCatNome('');
        setShowCatModal(true);
    };

    const abrirEditarCategoria = (c) => {
        setMensagem('');
        setCatEditId(c.id);
        setCatNome(c.nome || '');
        setShowCatModal(true);
    };

    const salvarCategoria = async () => {
        const nome = (catNome || '').trim();
        if (!nome) { setMensagem('Informe o nome da categoria.'); return; }
        verificandoToken();
        setSalvandoCat(true);
        setMensagem('');
        try {
            if (catEditId == null) await createCategoria({ nome });
            else await updateCategoria(catEditId, { nome });
            setShowCatModal(false);
            setMensagemOk(catEditId == null ? 'Categoria criada.' : 'Categoria renomeada.');
            await carregar();
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao salvar categoria.');
        } finally {
            setSalvandoCat(false);
        }
    };

    const excluirCategoria = async (c) => {
        if (!window.confirm(`Excluir a categoria "${c.nome}"? Isso só é possível se ela estiver vazia.`)) return;
        verificandoToken();
        setMensagem('');
        try {
            await deleteCategoria(c.id);
            if (catSelecionada === c.id) selecionarCategoria(undefined);
            setMensagemOk('Categoria excluída.');
            await carregar();
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao excluir categoria.');
        }
    };

    const salvarEdicao = async () => {
        const nome = (editNome || '').trim();
        if (!nome) {
            setMensagem('Informe o nome do repositório.');
            return;
        }
        verificandoToken();
        setSalvandoEdicao(true);
        setMensagem('');
        try {
            const atualizado = await updateRepositorio(id, {
                nome,
                descricao: (editDescricao || '').trim() || null,
            });
            if (atualizado) setRepo(atualizado);
            setShowEditar(false);
            setMensagemOk('Repositório atualizado.');
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao salvar alterações.');
        } finally {
            setSalvandoEdicao(false);
        }
    };

    return (
        <div className="app-layout">
            <Sidebar />
            <main className="main-content">
                <div className="container-fluid titulo cadastro-unificado repo-page">
                    <div className="repo-breadcrumb mb-2">
                        <button type="button" className="btn btn-link btn-sm px-0" onClick={() => history.push('/repositorios')}>
                            <i className="fas fa-arrow-left me-1" />Repositórios
                        </button>
                    </div>

                    <div className="page-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <div className="repo-header-info">
                            <h1 className="mb-0 repo-header-titulo">
                                <i className={`fas ${repo?.tipo === 'sprint' ? 'fa-tasks' : 'fa-folder'} me-2`} />
                                <span>{repo?.nome || 'Repositório'}</span>
                                {repo && (
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm repo-btn-editar-titulo"
                                        onClick={abrirEditar}
                                        title="Editar nome e descrição"
                                        aria-label="Editar nome e descrição do repositório"
                                    >
                                        <i className="fas fa-pen" />
                                    </button>
                                )}
                            </h1>
                            <p className="text-muted small mb-0 repo-header-desc">
                                {repo?.descricao ? repo.descricao : <span className="repo-sem-descricao">Sem descrição</span>}
                            </p>
                            {repo?.tipo === 'sprint' && repo?.sprint_nome && (
                                <p className="text-muted small mb-0"><i className="fas fa-link me-1" />Sprint: {repo.sprint_nome}</p>
                            )}
                        </div>
                        <button type="button" className="btn btn-primary btn-sm" onClick={abrirModalCommit}>
                            <i className="fas fa-upload me-1" />Enviar arquivos
                        </button>
                    </div>

                    {mensagemOk && <div className="alert alert-success py-2 my-2">{mensagemOk}</div>}
                    {mensagem && <div className="alert alert-danger py-2 my-2">{mensagem}</div>}

                    <div className="repo-toolbar-explorer d-flex flex-wrap align-items-center gap-2 mb-3">
                        <div className="repo-tabs">
                            <button type="button" className={`btn btn-sm ${aba === 'arquivos' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setAba('arquivos')}>
                                <i className="fas fa-folder-tree me-1" />Arquivos
                            </button>
                            <button type="button" className={`btn btn-sm ${aba === 'commits' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setAba('commits')}>
                                <i className="fas fa-history me-1" />Histórico ({commits.length})
                            </button>
                        </div>
                        <div className="repo-busca-wrap input-group input-group-sm flex-grow-1">
                            <span className="input-group-text" aria-hidden="true"><i className="fas fa-search" /></span>
                            <input
                                type="search"
                                className="form-control"
                                placeholder={aba === 'commits' ? 'Buscar no histórico (mensagem, autor, projeto…)' : 'Buscar arquivos, projetos ou categorias…'}
                                value={busca}
                                onChange={(e) => setBusca(e.target.value)}
                                aria-label="Buscar no repositório"
                            />
                            {busca.trim() && (
                                <button type="button" className="btn btn-outline-secondary" onClick={() => setBusca('')} title="Limpar busca">
                                    <i className="fas fa-times" />
                                </button>
                            )}
                        </div>
                        {termoBusca && aba === 'arquivos' && (
                            <span className="repo-busca-resultado small text-muted">
                                {arquivosNaBusca.length} arquivo(s) encontrado(s)
                            </span>
                        )}
                        {termoBusca && aba === 'commits' && (
                            <span className="repo-busca-resultado small text-muted">
                                {commitsFiltrados.length} commit(s)
                            </span>
                        )}
                    </div>

                    {loading && <p className="text-muted">Carregando...</p>}

                    {!loading && aba === 'arquivos' && (
                        <div className="repo-explorer">
                            {/* Coluna 1: categorias */}
                            <div className="repo-col repo-col-categorias">
                                <div className="repo-col-titulo repo-col-titulo-acao">
                                    <span>Categorias</span>
                                    <button type="button" className="repo-col-add" onClick={abrirNovaCategoria} title="Nova categoria">
                                        <i className="fas fa-plus" />
                                    </button>
                                </div>
                                <button type="button" className={`repo-nav-item ${catSelecionada === undefined ? 'ativo' : ''}`} onClick={() => selecionarCategoria(undefined)}>
                                    <span><i className="fas fa-layer-group me-2" />Todas</span>
                                    <span className="repo-badge">{arquivosNaBusca.length}</span>
                                </button>
                                {categoriasComContagem.map((c) => {
                                    const cor = corCategoria(c);
                                    const real = c.id != null;
                                    return (
                                        <div key={String(c.id)} className={`repo-cat-item repo-cat-${cor} ${catSelecionada === c.id ? 'ativo' : ''}`}>
                                            <button type="button" className="repo-cat-main" onClick={() => selecionarCategoria(c.id)}>
                                                <span className="repo-cat-chip"><i className={`fas ${c.icone || 'fa-folder'}`} /></span>
                                                <span className="repo-cat-nome">{c.nome}</span>
                                                <span className="repo-badge">{c.total}</span>
                                            </button>
                                            {real && (
                                                <div className="repo-cat-acoes">
                                                    <button type="button" title="Renomear" onClick={(e) => { e.stopPropagation(); abrirEditarCategoria(c); }}>
                                                        <i className="fas fa-pen" />
                                                    </button>
                                                    {c.total === 0 && (
                                                        <button type="button" title="Excluir (vazia)" onClick={(e) => { e.stopPropagation(); excluirCategoria(c); }}>
                                                            <i className="fas fa-trash" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Coluna 2: projetos */}
                            <div className="repo-col repo-col-projetos">
                                <div className="repo-col-titulo">Projetos</div>
                                <button type="button" className={`repo-nav-item ${projSelecionado == null ? 'ativo' : ''}`} onClick={() => setProjSelecionado(null)}>
                                    <span><i className="fas fa-asterisk me-2" />Todos</span>
                                </button>
                                {projetosDaCategoria.length === 0 && <div className="repo-vazio small">Nenhum projeto aqui.</div>}
                                {projetosDaCategoria.map((p) => (
                                    <button key={p.projeto} type="button"
                                        className={`repo-nav-item ${projSelecionado === p.projeto ? 'ativo' : ''}`}
                                        onClick={() => setProjSelecionado(p.projeto)}>
                                        <span><i className="fas fa-folder-open me-2" />{p.projeto}</span>
                                        <span className="repo-badge">{p.total}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Coluna 3: arquivos */}
                            <div className="repo-col repo-col-arquivos">
                                <div className="repo-col-titulo">Arquivos</div>
                                {arquivosVisiveis.length === 0 && (
                                    <div className="repo-estado-vazio">
                                        <i className={`fas ${termoBusca ? 'fa-magnifying-glass' : 'fa-folder-open'}`} />
                                        <p>{termoBusca ? 'Nenhum arquivo corresponde à busca.' : 'Nenhum arquivo ainda.'}</p>
                                        {!termoBusca && <span className="small text-muted">Use "Enviar arquivos" no topo.</span>}
                                    </div>
                                )}
                                {arquivosVisiveis.map((a) => {
                                    const nomeReal = a.nome_arquivo_atual && a.nome_arquivo_atual !== a.caminho ? a.nome_arquivo_atual : null;
                                    return (
                                        <div key={a.id} className="repo-arquivo-item">
                                            <div className="repo-arquivo-info" role="button" tabIndex={0} onClick={() => abrirDrawer(a)} onKeyDown={(e) => e.key === 'Enter' && abrirDrawer(a)}>
                                                <i className={`fas ${iconeArquivo(nomeReal || a.caminho)} repo-arquivo-icone`} />
                                                <div className="repo-arquivo-textos">
                                                    <span className="repo-arquivo-nome">
                                                        {a.caminho}
                                                        {a.tem_doc && <span className="repo-arquivo-doc-badge" title={a.doc_resumo || 'Documentado'}><i className="fas fa-book" /></span>}
                                                    </span>
                                                    <span className="repo-arquivo-sub">v{a.versao_atual} · {formatarBytes(a.tamanho_bytes)}{nomeReal ? ` · ${nomeReal}` : ''}{a.doc_resumo ? ` · ${a.doc_resumo}` : ''}</span>
                                                </div>
                                            </div>
                                            <div className="repo-arquivo-acoes">
                                                <button type="button" className="btn btn-sm btn-outline-secondary" title="Baixar versão atual"
                                                    onClick={() => baixarArquivo(id, a.id, nomeReal || a.caminho.split('/').pop())}>
                                                    <i className="fas fa-download" />
                                                </button>
                                                <button type="button" className="btn btn-sm btn-outline-primary" title="Histórico e documentação" onClick={() => abrirDrawer(a, 'versoes')}>
                                                    <i className="fas fa-history" />
                                                </button>
                                                <button type="button" className="btn btn-sm btn-outline-info" title="Documentação do arquivo" onClick={() => abrirDrawer(a, 'doc')}>
                                                    <i className="fas fa-book" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {!loading && aba === 'commits' && commits.length > 0 && (
                        <div className="repo-hist-filtros">
                            <div className="repo-hist-filtro">
                                <label>Categoria</label>
                                <select className="form-control form-control-sm" value={filtroCommitCat} onChange={(e) => setFiltroCommitCat(e.target.value)}>
                                    <option value="">Todas</option>
                                    {opcoesHistorico.categorias.map((n) => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                            <div className="repo-hist-filtro">
                                <label>Projeto</label>
                                <select className="form-control form-control-sm" value={filtroCommitProj} onChange={(e) => setFiltroCommitProj(e.target.value)}>
                                    <option value="">Todos</option>
                                    {opcoesHistorico.projetos.map((p) => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>
                            <div className="repo-hist-filtro">
                                <label>Arquivo</label>
                                <select className="form-control form-control-sm" value={filtroCommitArq} onChange={(e) => setFiltroCommitArq(e.target.value)}>
                                    <option value="">Todos</option>
                                    {opcoesHistorico.arquivos.map((a) => <option key={a} value={a}>{a}</option>)}
                                </select>
                            </div>
                            <div className="repo-hist-filtro">
                                <label>De</label>
                                <input type="date" className="form-control form-control-sm" value={filtroDataIni} onChange={(e) => setFiltroDataIni(e.target.value)} />
                            </div>
                            <div className="repo-hist-filtro">
                                <label>Até</label>
                                <input type="date" className="form-control form-control-sm" value={filtroDataFim} onChange={(e) => setFiltroDataFim(e.target.value)} />
                            </div>
                            {(filtroCommitCat || filtroCommitProj || filtroCommitArq || filtroDataIni || filtroDataFim) && (
                                <button type="button" className="btn btn-link btn-sm repo-hist-limpar" onClick={() => { setFiltroCommitCat(''); setFiltroCommitProj(''); setFiltroCommitArq(''); setFiltroDataIni(''); setFiltroDataFim(''); }}>
                                    <i className="fas fa-times me-1" />Limpar filtros
                                </button>
                            )}
                        </div>
                    )}
                    {!loading && aba === 'commits' && (
                        <div className="repo-commits">
                            {commits.length === 0 && <div className="alert alert-info py-3">Nenhum commit ainda.</div>}
                            {commits.length > 0 && commitsFiltrados.length === 0 && (
                                <div className="alert alert-info py-3">Nenhum commit corresponde aos filtros.</div>
                            )}
                            {commitsFiltrados.map((c) => (
                                <div key={c.id} className="repo-commit-item">
                                    <div className="repo-commit-marker"><i className="fas fa-code-commit" /></div>
                                    <div className="repo-commit-corpo">
                                        <div className="repo-commit-msg">{c.mensagem || '(sem mensagem)'}</div>
                                        <div className="repo-commit-meta small text-muted">
                                            <i className="fas fa-user me-1" />{c.autor_nome || '—'}
                                            <span className="mx-2">·</span>
                                            <i className="fas fa-clock me-1" />{formatarDataHora(c.criado_em)}
                                            <span className="mx-2">·</span>
                                            {c.total_alteracoes} alteração(ões)
                                            {c.sat_num ? <><span className="mx-2">·</span><i className="fas fa-ticket-alt me-1" />Chamado {c.sat_num}</> : null}
                                        </div>
                                        {((c.categorias && c.categorias.length) || (c.projetos && c.projetos.length)) ? (
                                            <div className="repo-commit-tags">
                                                {(c.categorias || []).map((n) => <span key={'c' + n} className="repo-commit-tag tag-cat"><i className="fas fa-folder me-1" />{n}</span>)}
                                                {(c.projetos || []).map((p) => <span key={'p' + p} className="repo-commit-tag tag-proj"><i className="fas fa-diagram-project me-1" />{p}</span>)}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            {/* Modal: criar / renomear categoria */}
            {showCatModal && (
                <div className="modal-overlay" onClick={() => !salvandoCat && setShowCatModal(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h5 className="mb-3">
                            <i className={`fas ${catEditId == null ? 'fa-folder-plus' : 'fa-pen'} me-2`} />
                            {catEditId == null ? 'Nova categoria' : 'Renomear categoria'}
                        </h5>
                        <div className="form-group mb-2">
                            <label>Nome da categoria</label>
                            <input value={catNome} onChange={(e) => setCatNome(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && salvarCategoria()}
                                className="form-control form-control-sm" placeholder="Ex.: Integrações" autoFocus />
                        </div>
                        {mensagem && <div className="alert alert-danger py-2 mb-2">{mensagem}</div>}
                        <div className="d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-secondary btn-sm" disabled={salvandoCat} onClick={() => setShowCatModal(false)}>Cancelar</button>
                            <button type="button" className="btn btn-primary btn-sm" disabled={salvandoCat} onClick={salvarCategoria}>
                                {salvandoCat ? 'Salvando...' : (catEditId == null ? 'Criar' : 'Salvar')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de commit */}
            {showCommit && (
                <div className="modal-overlay" onClick={() => !enviando && setShowCommit(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h5 className="mb-3"><i className="fas fa-upload me-2" />Enviar arquivos</h5>
                        <div className="form-group mb-2">
                            <label>Arquivos</label>
                            <input type="file" multiple className="form-control form-control-sm"
                                onChange={(e) => setCommitFiles(Array.from(e.target.files || []))} />
                            {commitFiles.length > 0 && <small className="text-muted">{commitFiles.length} arquivo(s) selecionado(s)</small>}
                        </div>
                        <div className="form-group mb-2">
                            <label>Categoria (tipo de desenvolvimento)</label>
                            <select value={commitCategoria} onChange={(e) => setCommitCategoria(e.target.value)} className="form-control form-control-sm">
                                <option value="">Sem categoria</option>
                                {categorias.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                            </select>
                        </div>
                        <div className="form-group mb-2">
                            <label>Projeto</label>
                            <input list="repo-projetos" value={commitProjeto} onChange={(e) => setCommitProjeto(e.target.value)}
                                className="form-control form-control-sm" placeholder="Ex.: Integração NFe" />
                            <datalist id="repo-projetos">
                                {projetosExistentes.map((p) => <option key={p} value={p} />)}
                            </datalist>
                        </div>
                        <div className="form-group mb-2">
                            <label>Mensagem do commit</label>
                            <input value={commitMensagem} onChange={(e) => setCommitMensagem(e.target.value)}
                                className="form-control form-control-sm" placeholder="Ex.: Ajuste na regra de cálculo" />
                        </div>
                        {commitTemArquivoNovo && (
                            <div className="form-group mb-2">
                                <label>Senha para exclusão</label>
                                <input type="password" value={commitSenhaExclusao} onChange={(e) => setCommitSenhaExclusao(e.target.value)}
                                    className="form-control form-control-sm" placeholder="Mín. 4 caracteres" autoComplete="new-password" />
                                <small className="text-muted">Obrigatória para arquivos novos. Será exigida para excluir o arquivo depois.</small>
                            </div>
                        )}
                        {mensagem && <div className="alert alert-danger py-2 mb-2">{mensagem}</div>}
                        <div className="d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-secondary btn-sm" disabled={enviando} onClick={() => setShowCommit(false)}>Cancelar</button>
                            <button type="button" className="btn btn-primary btn-sm" disabled={enviando} onClick={enviarCommit}>
                                {enviando ? 'Enviando...' : 'Enviar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showEditar && (
                <div className="modal-overlay" onClick={() => !salvandoEdicao && setShowEditar(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h5 className="mb-3"><i className="fas fa-pen me-2" />Editar repositório</h5>
                        <div className="form-group mb-2">
                            <label>Nome</label>
                            <input
                                value={editNome}
                                onChange={(e) => setEditNome(e.target.value)}
                                className="form-control form-control-sm"
                                placeholder="Ex.: Senior ERP - Orsegups"
                                autoFocus
                                disabled={salvandoEdicao}
                            />
                        </div>
                        <div className="form-group mb-2">
                            <label>Descrição (opcional)</label>
                            <textarea
                                value={editDescricao}
                                onChange={(e) => setEditDescricao(e.target.value)}
                                className="form-control form-control-sm"
                                rows={3}
                                placeholder="Para que serve este repositório"
                                disabled={salvandoEdicao}
                            />
                        </div>
                        {mensagem && showEditar && <div className="alert alert-danger py-2 mb-2">{mensagem}</div>}
                        <div className="d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-secondary btn-sm" disabled={salvandoEdicao} onClick={() => setShowEditar(false)}>Cancelar</button>
                            <button type="button" className="btn btn-primary btn-sm" disabled={salvandoEdicao} onClick={salvarEdicao}>
                                {salvandoEdicao ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Drawer de versões */}
            {drawerArquivo && (
                <div className="repo-drawer-overlay" onClick={fecharDrawer}>
                    <div className="repo-drawer" onClick={(e) => e.stopPropagation()}>
                        <div className="repo-drawer-header">
                            <div>
                                <div className="repo-drawer-titulo"><i className="fas fa-file me-2" />{drawerArquivo.caminho}</div>
                                <div className="repo-drawer-tabs">
                                    <button type="button" className={`repo-drawer-tab ${drawerAba === 'versoes' ? 'ativo' : ''}`} onClick={() => abrirDrawer(drawerArquivo, 'versoes')}>
                                        <i className="fas fa-history me-1" />Versões
                                    </button>
                                    <button type="button" className={`repo-drawer-tab ${drawerAba === 'doc' ? 'ativo' : ''}`} onClick={() => setDrawerAba('doc')}>
                                        <i className="fas fa-book me-1" />Documentação
                                    </button>
                                </div>
                            </div>
                            <div className="repo-drawer-header-acoes">
                                {!drawerArquivo.excluido && (
                                    <button type="button" className="repo-drawer-btn-excluir" title="Excluir (mantém histórico)" onClick={abrirExcluirArquivo}>
                                        <i className="fas fa-trash" />
                                    </button>
                                )}
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={fecharDrawer} title="Fechar"><i className="fas fa-times" /></button>
                            </div>
                        </div>
                        <div className="repo-drawer-acoes">
                            {drawerAba === 'versoes' && !drawerArquivo.excluido && (
                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={abrirAtualizarVersao}>
                                    <i className="fas fa-upload me-1" />Atualizar com nova versão
                                </button>
                            )}
                        </div>
                        <div className="repo-drawer-body">
                            {drawerAba === 'doc' ? (
                                <RepoArquivoDoc
                                    repoId={id}
                                    arquivo={drawerArquivo}
                                    onNavigateToArquivo={navegarDocArquivo}
                                    onSaved={carregar}
                                />
                            ) : (
                                <>
                            {loadingVersoes && <p className="text-muted">Carregando versões...</p>}
                            {!loadingVersoes && versoes.length === 0 && <p className="text-muted">Sem versões.</p>}
                            {!loadingVersoes && versoes.map((v) => (
                                <div key={v.id} className="repo-versao-item">
                                    <div className="repo-versao-top">
                                        <span className={`badge ${ACAO_BADGE[v.acao] || 'bg-secondary'}`}>v{v.versao} · {ACAO_LABEL[v.acao] || v.acao}</span>
                                        <span className="small text-muted">{formatarDataHora(v.criado_em)}</span>
                                    </div>
                                    <div className="repo-versao-meta small text-muted">
                                        <i className="fas fa-user me-1" />{v.autor_nome || '—'}
                                        {v.tamanho_bytes ? <><span className="mx-2">·</span>{formatarBytes(v.tamanho_bytes)}</> : null}
                                    </div>
                                    {v.mensagem && <div className="repo-versao-msg small">{v.mensagem}</div>}
                                    <div className="repo-versao-acoes">
                                        {v.blob_id !== null && v.acao !== 'delete' && (
                                            <button type="button" className="btn btn-sm btn-outline-secondary"
                                                onClick={() => baixarArquivo(id, drawerArquivo.id, v.nome_arquivo || drawerArquivo.caminho.split('/').pop(), v.versao)}>
                                                <i className="fas fa-download me-1" />Baixar
                                            </button>
                                        )}
                                        {v.versao !== drawerArquivo.versao_atual && (
                                            <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => restaurar(drawerArquivo, v.versao)}>
                                                <i className="fas fa-undo me-1" />Restaurar esta versão
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal acima do drawer: excluir arquivo */}
            {showExcluirArquivo && drawerArquivo && (
                <div className="modal-overlay repo-modal-over-drawer" onClick={() => !excluindoArquivo && setShowExcluirArquivo(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h5 className="mb-3"><i className="fas fa-trash me-2" />Excluir arquivo</h5>
                        <p className="small text-muted mb-3">
                            O arquivo <strong>{drawerArquivo.caminho}</strong> será excluído, mas o histórico de versões permanece para recuperação.
                        </p>
                        {drawerArquivo.tem_senha_exclusao ? (
                            <div className="form-group mb-2">
                                <label>Senha de exclusão</label>
                                <input type="password" value={senhaExclusaoInput} onChange={(e) => setSenhaExclusaoInput(e.target.value)}
                                    className="form-control form-control-sm" placeholder="Senha definida ao cadastrar o arquivo"
                                    autoComplete="current-password" autoFocus />
                            </div>
                        ) : (
                            <p className="small text-muted mb-2">Este arquivo foi cadastrado antes da proteção por senha e pode ser excluído sem senha.</p>
                        )}
                        {mensagem && showExcluirArquivo && <div className="alert alert-danger py-2 mb-2">{mensagem}</div>}
                        <div className="d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-secondary btn-sm" disabled={excluindoArquivo} onClick={() => setShowExcluirArquivo(false)}>Cancelar</button>
                            <button type="button" className="btn btn-danger btn-sm" disabled={excluindoArquivo} onClick={confirmarExcluirArquivo}>
                                {excluindoArquivo ? 'Excluindo...' : 'Excluir'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal acima do drawer: atualizar versão de um arquivo existente */}
            {showAtualizarVersao && drawerArquivo && (
                <div className="modal-overlay repo-modal-over-drawer" onClick={() => !enviandoAtualizacao && setShowAtualizarVersao(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h5 className="mb-3"><i className="fas fa-upload me-2" />Atualizar com nova versão</h5>
                        <div className="form-group mb-2">
                            <label>Nome no repositório</label>
                            <input type="text" className="form-control form-control-sm" value={drawerArquivo.caminho} readOnly disabled />
                            <small className="text-muted">O arquivo enviado será registrado com este nome, mesmo que o arquivo local tenha outro nome.</small>
                        </div>
                        <div className="form-group mb-2">
                            <label>Arquivo</label>
                            <input type="file" className="form-control form-control-sm"
                                onChange={(e) => setAtualizarFile((e.target.files && e.target.files[0]) || null)} />
                            {atualizarFile && (
                                <small className="text-muted d-block mt-1">
                                    Selecionado: {atualizarFile.name}
                                </small>
                            )}
                        </div>
                        <div className="form-group mb-2">
                            <label>Mensagem (opcional)</label>
                            <input value={atualizarMensagem} onChange={(e) => setAtualizarMensagem(e.target.value)}
                                className="form-control form-control-sm" placeholder="Ex.: Correção na regra de negociação" />
                        </div>
                        {mensagem && showAtualizarVersao && <div className="alert alert-danger py-2 mb-2">{mensagem}</div>}
                        <div className="d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-secondary btn-sm" disabled={enviandoAtualizacao} onClick={() => setShowAtualizarVersao(false)}>Cancelar</button>
                            <button type="button" className="btn btn-primary btn-sm" disabled={enviandoAtualizacao} onClick={enviarAtualizacaoVersao}>
                                {enviandoAtualizacao ? 'Enviando...' : 'Enviar nova versão'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Repositorio;
